import { useEffect, useState } from "react";
import api from "../lib/api";
import { Avatar, Username, VipBadge } from "../components/UserBadge";
import { Trophy, Crown, Calendar, Flame, Award, Star, Activity, Sparkles, MessageSquare, Zap } from "lucide-react";
import { Link } from "react-router-dom";

export default function Leaderboard() {
  const [monthly, setMonthly] = useState({ leaders: [] });
  const [all, setAll] = useState([]);
  const [winners, setWinners] = useState([]);
  const [stats, setStats] = useState({});
  const [activeTab, setActiveTab] = useState("alltime"); // alltime, monthly, winners

  useEffect(() => {
    api.get("/leaderboard/monthly").then((r) => setMonthly(r.data)).catch(() => {});
    api.get("/users/top?limit=25").then((r) => setAll(r.data)).catch(() => {});
    api.get("/leaderboard/monthly-winners").then((r) => setWinners(r.data)).catch(() => {});
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const displayList = activeTab === "monthly" 
    ? (monthly.leaders || []) 
    : activeTab === "winners" 
      ? winners 
      : all;

  const top3 = (activeTab === "winners" ? winners.map(w => w.user).filter(Boolean) : displayList).slice(0, 3);
  const remainingList = (activeTab === "winners" ? winners : displayList).slice(3);

  return (
    <div className="max-w-[1500px] mx-auto space-y-6" data-testid="leaderboard-page">
      {/* 1. Hero Command Header */}
      <div className="bg-[#0b0e15] border border-white/10 p-4 sm:p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 relative overflow-hidden shadow-2xl">
        <div className="absolute top-0 right-0 w-80 h-full bg-gradient-to-l from-[var(--tf-primary)]/10 via-transparent to-transparent pointer-events-none" />
        <div className="flex items-center gap-3 relative z-10">
          <div 
            className="w-12 h-12 flex items-center justify-center text-black font-black shrink-0 shadow-lg"
            style={{ backgroundColor: "var(--tf-primary)" }}
          >
            <Trophy className="w-6 h-6" />
          </div>
          <div>
            <h1 className="font-chakra text-2xl sm:text-3xl font-black uppercase tracking-wider text-white">
              TOP JUCĂTORI & STATISTICI
            </h1>
            <p className="text-xs text-zinc-400 font-mono">
              // Clasamentul oficial de activitate, reputație și puncte din comunitatea INDUNGI
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 relative z-10">
          <button
            onClick={() => setActiveTab("alltime")}
            className={`px-3.5 py-2 text-xs font-chakra font-black uppercase tracking-wider transition-all border ${
              activeTab === "alltime"
                ? "bg-[var(--tf-primary)] text-black border-[var(--tf-primary)] shadow-md"
                : "bg-[#07090e] border-white/10 text-zinc-400 hover:text-white"
            }`}
          >
            All-Time Top
          </button>
          <button
            onClick={() => setActiveTab("monthly")}
            className={`px-3.5 py-2 text-xs font-chakra font-black uppercase tracking-wider transition-all border ${
              activeTab === "monthly"
                ? "bg-[var(--tf-primary)] text-black border-[var(--tf-primary)] shadow-md"
                : "bg-[#07090e] border-white/10 text-zinc-400 hover:text-white"
            }`}
          >
            Luna Curentă
          </button>
          <button
            onClick={() => setActiveTab("winners")}
            className={`px-3.5 py-2 text-xs font-chakra font-black uppercase tracking-wider transition-all border ${
              activeTab === "winners"
                ? "bg-[var(--tf-primary)] text-black border-[var(--tf-primary)] shadow-md"
                : "bg-[#07090e] border-white/10 text-zinc-400 hover:text-white"
            }`}
          >
            Câștigători
          </button>
        </div>
      </div>

      {/* 2. Telemetry Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-[#0b0e15] border border-white/10 p-4">
          <span className="text-[10px] text-zinc-400 font-chakra font-bold uppercase tracking-wider block">Total Membri</span>
          <span className="font-chakra font-black text-2xl text-white mt-1 block">{stats.users || 0}</span>
        </div>
        <div className="bg-[#0b0e15] border border-white/10 p-4">
          <span className="text-[10px] text-zinc-400 font-chakra font-bold uppercase tracking-wider block">Topicuri Deschise</span>
          <span className="font-chakra font-black text-2xl text-white mt-1 block">{stats.topics || 0}</span>
        </div>
        <div className="bg-[#0b0e15] border border-white/10 p-4">
          <span className="text-[10px] text-zinc-400 font-chakra font-bold uppercase tracking-wider block">Răspunsuri & Mesaje</span>
          <span className="font-chakra font-black text-2xl text-white mt-1 block">{stats.posts || 0}</span>
        </div>
        <div className="bg-[#0b0e15] border border-[var(--tf-primary)]/40 p-4 relative overflow-hidden">
          <span className="text-[10px] text-[var(--tf-primary)] font-chakra font-bold uppercase tracking-wider block">Membri VIP & Donatori</span>
          <span className="font-chakra font-black text-2xl text-white mt-1 block">{stats.vip_count || 12}</span>
        </div>
      </div>

      {/* 3. Podium Top 3 */}
      {top3.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
          {/* Silver #2 */}
          {top3[1] && (
            <div className="bg-[#0b0e15] border border-zinc-500/40 p-5 flex flex-col items-center justify-between text-center relative md:order-1 order-2">
              <div className="absolute top-3 left-3 text-xl font-black font-chakra text-zinc-400">#2</div>
              <div className="text-2xl mb-2">🥈</div>
              <Avatar user={top3[1]} size={64} />
              <div className="my-3">
                <Link to={`/u/${top3[1].username}`} className="font-chakra font-black text-base text-white hover:text-[var(--tf-primary)] transition-colors">
                  {top3[1].username}
                </Link>
                <div className="text-xs font-mono text-zinc-400 mt-1">
                  {top3[1].points?.toLocaleString() || 0} Puncte
                </div>
              </div>
              <span className="px-2.5 py-1 bg-zinc-500/20 text-zinc-300 font-black font-chakra text-[10px] uppercase tracking-wider">
                ARGINT
              </span>
            </div>
          )}

          {/* Gold #1 */}
          {top3[0] && (
            <div className="bg-[#0b0e15] border-2 border-[var(--tf-primary)] p-6 flex flex-col items-center justify-between text-center relative md:order-2 order-1 shadow-[0_0_30px_rgba(255,79,0,0.15)]">
              <div 
                className="absolute -top-3 px-3 py-0.5 text-black font-black font-chakra text-xs uppercase tracking-wider shadow-md"
                style={{ backgroundColor: "var(--tf-primary)" }}
              >
                CAMPIONUL #1
              </div>
              <div className="text-3xl mb-2 mt-1">👑 🥇</div>
              <Avatar user={top3[0]} size={76} />
              <div className="my-3">
                <Link to={`/u/${top3[0].username}`} className="font-chakra font-black text-lg text-white hover:text-[var(--tf-primary)] transition-colors">
                  {top3[0].username}
                </Link>
                <div className="text-xs font-mono text-[var(--tf-primary)] font-bold mt-1">
                  {top3[0].points?.toLocaleString() || 0} Puncte
                </div>
              </div>
              <span 
                className="px-3 py-1 text-black font-black font-chakra text-[11px] uppercase tracking-wider"
                style={{ backgroundColor: "var(--tf-primary)" }}
              >
                LOCUL 1 AUR
              </span>
            </div>
          )}

          {/* Bronze #3 */}
          {top3[2] && (
            <div className="bg-[#0b0e15] border border-amber-700/40 p-5 flex flex-col items-center justify-between text-center relative md:order-3 order-3">
              <div className="absolute top-3 left-3 text-xl font-black font-chakra text-amber-600">#3</div>
              <div className="text-2xl mb-2">🥉</div>
              <Avatar user={top3[2]} size={64} />
              <div className="my-3">
                <Link to={`/u/${top3[2].username}`} className="font-chakra font-black text-base text-white hover:text-[var(--tf-primary)] transition-colors">
                  {top3[2].username}
                </Link>
                <div className="text-xs font-mono text-zinc-400 mt-1">
                  {top3[2].points?.toLocaleString() || 0} Puncte
                </div>
              </div>
              <span className="px-2.5 py-1 bg-amber-700/20 text-amber-500 font-black font-chakra text-[10px] uppercase tracking-wider">
                BRONZ
              </span>
            </div>
          )}
        </div>
      )}

      {/* 4. Full Ranking Table */}
      <div className="bg-[#0b0e15] border border-white/10">
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <h3 className="font-chakra font-black text-sm uppercase tracking-wider text-white">
            {activeTab === "monthly" ? "Top Activitate Luna Curentă" : activeTab === "winners" ? "Istoric Câștigători Lunari" : "Clasament General Membri"}
          </h3>
          <span className="text-xs font-mono text-zinc-500">Top 25 Membri</span>
        </div>

        <div className="divide-y divide-white/5">
          {(activeTab === "winners" ? winners : displayList).map((item, idx) => {
            const u = activeTab === "winners" ? item.user : item;
            if (!u) return null;
            const rank = idx + 1;
            const points = activeTab === "monthly" ? `${item.monthly_points || 0} pts` : activeTab === "winners" ? `${item.monthly_points || 0} pts (${item.month_key})` : `${u.points || 0} pts`;

            return (
              <div 
                key={u.id || idx}
                className="p-3.5 flex items-center gap-4 hover:bg-[#0e131d] transition-colors"
              >
                <span className={`w-8 text-center font-chakra font-black text-sm ${rank <= 3 ? "text-[var(--tf-primary)]" : "text-zinc-500"}`}>
                  #{rank}
                </span>

                <Link to={`/u/${u.username}`} className="shrink-0">
                  <Avatar user={u} size={40} />
                </Link>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Link to={`/u/${u.username}`} className="font-chakra font-bold text-sm text-white hover:text-[var(--tf-primary)] transition-colors truncate">
                      {u.username}
                    </Link>
                    {((u.loadout?.banner_badge && u.loadout.banner_badge !== "none") || (u.vip_tier && u.vip_tier !== "none")) && (
                      <VipBadge tier={u.loadout?.banner_badge || u.vip_tier} />
                    )}
                  </div>
                  <div className="text-[11px] font-mono text-zinc-500">
                    {u.message_count || 0} postări · Membru din {u.joined_at ? new Date(u.joined_at).getFullYear() : "2026"}
                  </div>
                </div>

                <div className="text-right">
                  <span className="font-mono text-xs font-bold text-white block">{points}</span>
                  <span className="text-[10px] font-mono text-emerald-400">{u.reaction_score || 0} reacții</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
